/**
 * Text-to-Speech Service
 * Handles voice synthesis for AI responses
 */

import api from './api';

/**
 * Convert text to speech and return audio blob
 * @param {string} text - Text to convert to speech
 * @param {object} options - Voice options
 * @param {string} options.voiceName - Voice name (e.g., 'en-US-Wavenet-D')
 * @param {number} options.speakingRate - Speaking rate (0.25 to 4.0)
 * @param {number} options.pitch - Pitch adjustment (-20.0 to 20.0)
 * @returns {Promise<Blob>} Audio blob (MP3 format)
 */
export const textToSpeech = async (text, options = {}) => {
    try {
        // Use authRequest for authenticated endpoint with blob response
        const response = await api.authRequest('/tts/synthesize', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                text,
                voice_name: options.voiceName || 'en-US-Wavenet-D',
                speaking_rate: options.speakingRate || 1.1,
                pitch: options.pitch || 0.0
            })
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        // Get the audio blob from the response
        const audioBlob = await response.blob();
        return audioBlob;
    } catch (error) {
        console.error('TTS error:', error);
        throw new Error(`Voice synthesis failed: ${error.message}`);
    }
};

/**
 * Get available TTS voices
 * @param {string} languageCode - Language code (e.g., 'en-US')
 * @returns {Promise<Array>} List of available voices
 */
export const getAvailableVoices = async (languageCode = 'en-US') => {
    try {
        const response = await api.authRequest(`/tts/voices?language_code=${languageCode}`, {
            method: 'GET'
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        // Handle ApiResponse format
        if (data.success && data.data) {
            return data.data.voices || [];
        }
        return data.voices || [];
    } catch (error) {
        console.error('Failed to get voices:', error);
        return [];
    }
};

/**
 * Get current TTS configuration
 * @returns {Promise<object>} TTS configuration
 */
export const getTTSConfig = async () => {
    try {
        const response = await api.authRequest('/tts/config', {
            method: 'GET'
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        // Handle ApiResponse format
        if (data.success && data.data) {
            return data.data;
        }
        return data;
    } catch (error) {
        console.error('Failed to get TTS config:', error);
        return null;
    }
};

/**
 * Play text as speech immediately
 * @param {string} text - Text to speak
 * @param {object} options - Voice options
 * @returns {Promise<HTMLAudioElement>} Audio element
 */
export const speakText = async (text, options = {}) => {
    const audioBlob = await textToSpeech(text, options);
    const audioUrl = URL.createObjectURL(audioBlob);
    
    const audio = new Audio(audioUrl);
    
    // Clean up URL when audio finishes
    audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
    };
    
    // Clean up on error too
    audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
    };
    
    await audio.play();
    return audio;
};

/**
 * Speech queue manager for sequential playback
 * Ensures audio clips play one at a time without overlap
 */
class SpeechQueue {
    constructor() {
        this.queue = [];
        this.isPlaying = false;
        this.currentAudio = null;
        this.onPlayingChange = null; // Callback for playing state changes
        this.onInterrupted = null; // Callback when audio is interrupted
        this.interruptionEnabled = true; // Can be disabled temporarily
    }
    
    /**
     * Add text to speech queue
     * @param {string} text - Text to speak
     * @param {object} options - Voice options
     * @returns {Promise} Resolves when audio finishes playing
     */
    async add(text, options = {}) {
        return new Promise((resolve, reject) => {
            this.queue.push({ text, options, resolve, reject });
            if (!this.isPlaying) {
                this.playNext();
            }
        });
    }
    
    /**
     * Play next item in queue
     * @private
     */
    async playNext() {
        if (this.queue.length === 0) {
            this.isPlaying = false;
            this.currentAudio = null;
            if (this.onPlayingChange) {
                this.onPlayingChange(false);
            }
            return;
        }
        
        this.isPlaying = true;
        if (this.onPlayingChange) {
            this.onPlayingChange(true);
        }
        
        const { text, options, resolve, reject } = this.queue.shift();
        
        try {
            const audioBlob = await textToSpeech(text, options);
            const audioUrl = URL.createObjectURL(audioBlob);
            
            this.currentAudio = new Audio(audioUrl);
            
            this.currentAudio.onended = () => {
                URL.revokeObjectURL(audioUrl);
                resolve();
                this.playNext();
            };
            
            this.currentAudio.onerror = (error) => {
                URL.revokeObjectURL(audioUrl);
                reject(new Error('Audio playback failed'));
                this.playNext();
            };
            
            await this.currentAudio.play();
            
        } catch (error) {
            console.error('Speech synthesis error:', error);
            reject(error);
            this.playNext();
        }
    }
    
    /**
     * Immediately interrupt current audio playback (for voice interruption)
     * @param {string} reason - Reason for interruption (for logging)
     * @returns {boolean} True if audio was interrupted, false if nothing was playing
     */
    interrupt(reason = 'User interrupted') {
        console.log(`🛑 === SPEECHQUEUE INTERRUPT ===`);
        console.log(`🛑 Reason: ${reason}`);
        console.log(`🛑 Current state:`, {
            isPlaying: this.isPlaying,
            hasCurrentAudio: !!this.currentAudio,
            queueLength: this.queue.length,
            interruptionEnabled: this.interruptionEnabled
        });
        
        if (!this.interruptionEnabled) {
            console.log('⚠️ Interruption disabled, ignoring interrupt request');
            return false;
        }
        
        let wasPlaying = false;
        
        if (this.currentAudio) {
            console.log('🔇 Stopping current speechQueue audio playback');
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0; // Reset to beginning
            this.currentAudio = null;
            wasPlaying = true;
        } else {
            console.log('ℹ️ No current audio in speechQueue');
        }
        
        if (this.queue.length > 0) {
            console.log(`🗑️ Clearing ${this.queue.length} queued audio items`);
            // Reject all queued promises
            this.queue.forEach(item => {
                item.reject(new Error(`Audio interrupted: ${reason}`));
            });
            this.queue = [];
        }
        
        this.isPlaying = false;
        
        if (this.onPlayingChange) {
            this.onPlayingChange(false);
        }
        
        if (this.onInterrupted && wasPlaying) {
            this.onInterrupted(reason);
        }
        
        console.log(`🛑 SpeechQueue interrupt complete, wasPlaying: ${wasPlaying}`);
        return wasPlaying;
    }
    
    /**
     * Stop current audio and clear queue
     */
    stop() {
        return this.interrupt('Manual stop');
    }
    
    /**
     * Clear queue but let current audio finish
     */
    clear() {
        this.queue = [];
    }
    
    /**
     * Get queue length
     * @returns {number} Number of items in queue
     */
    getQueueLength() {
        return this.queue.length;
    }
    
    /**
     * Check if currently playing
     * @returns {boolean} True if audio is playing
     */
    getIsPlaying() {
        return this.isPlaying;
    }
    
    /**
     * Enable or disable interruption capability
     * @param {boolean} enabled - Whether interruption should be allowed
     */
    setInterruptionEnabled(enabled) {
        this.interruptionEnabled = enabled;
        console.log(`🔧 Audio interruption ${enabled ? 'enabled' : 'disabled'}`);
    }
    
    /**
     * Set callback for playing state changes
     * @param {Function} callback - Called with (isPlaying) when state changes
     */
    setOnPlayingChange(callback) {
        this.onPlayingChange = callback;
    }
    
    /**
     * Set callback for interruption events
     * @param {Function} callback - Called with (reason) when audio is interrupted
     */
    setOnInterrupted(callback) {
        this.onInterrupted = callback;
    }
}

// Export singleton speech queue instance
export const speechQueue = new SpeechQueue();

export default {
    textToSpeech,
    speakText,
    getAvailableVoices,
    getTTSConfig,
    speechQueue
};
